import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, CheckCircle, ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import allServices, { getServiceBySlug } from "@/data/services";

function ServiceStructuredData({ service }) {
  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": service.seoTitle.split(" | ")[0],
        "provider": {
          "@type": "Organization",
          "name": "Vaana BuildLogic Technologies",
          "url": "https://www.vaana.ai.in"
        },
        "areaServed": "India"
      }
    ];

    if (service.faq) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
          "@type": "Question",
          "name": service.faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": service.faq.answer }
        }]
      });
    }

    const elements = schemas.map((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = `service-json-ld-${index}`;
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });

    return () => elements.forEach((element) => element.remove());
  }, [service]);

  return null;
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  usePageMeta(service?.seoTitle, service?.seoDescription, service?.seoKeywords, false);

  if (!service) return <Navigate to="/services" replace />;

  const currentIdx = allServices.findIndex(s => s.slug === slug);
  const prev = currentIdx > 0 ? allServices[currentIdx - 1] : null;
  const next = currentIdx < allServices.length - 1 ? allServices[currentIdx + 1] : null;
  const related = allServices.filter(s => s.category === service.category && s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="service-detail-page" className="pt-20">
      <ServiceStructuredData service={service} />
      {/* Hero */}
      <section className="relative h-[400px] overflow-hidden">
        <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D72B8] via-[#0D72B8]/80 to-[#0D72B8]/40" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full pb-12">
            <Link to="/services" className="inline-flex items-center gap-2 text-blue-200 text-sm mb-4 hover:text-white transition-colors">
              <ArrowLeft size={14} /> All Services
            </Link>
            {service.tag && <span className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white bg-white/20 backdrop-blur-sm rounded-full mb-4 ml-4">{service.tag}</span>}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Sora'] font-bold text-white">{service.heroText}</h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <p className="text-lg text-[#4B5563] leading-relaxed mb-4">{service.seoContent}</p>
              <p className="text-lg text-[#4B5563] leading-relaxed mb-10">{service.desc}</p>

              <h2 className="text-2xl font-['Sora'] font-semibold text-[#111827] mb-6">What We Offer</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
                {service.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-[#F9FAFB] rounded-xl border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#0D72B8] to-[#2B8AD4] flex-shrink-0 mt-0.5">
                      <CheckCircle size={14} className="text-white" />
                    </div>
                    <span className="text-sm text-[#4B5563] font-medium">{f}</span>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-['Sora'] font-semibold text-[#111827] mb-6">Key Benefits</h2>
              <ul className="space-y-4 mb-12">
                {service.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-50 text-[#0D72B8] text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-[#4B5563]">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3 btn-solid text-sm font-semibold rounded-full">
                  Get a Quote <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3 btn-outline-blue text-sm font-semibold rounded-full">
                  Schedule a Consultation
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className={`p-8 rounded-2xl bg-gradient-to-br ${service.color} text-white`}>
                <service.icon size={36} className="text-white mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-['Sora'] font-semibold mb-3">{service.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6">{service.shortDesc}</p>
                <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#0D72B8] text-sm font-semibold rounded-full hover:bg-gray-50 transition-all">
                  Enquire Now <ArrowRight size={14} />
                </Link>
              </div>

              {related.length > 0 && (
                <div className="p-6 bg-[#F9FAFB] rounded-2xl border border-gray-200">
                  <h4 className="text-sm font-semibold text-[#111827] mb-4">Related Services</h4>
                  <ul className="space-y-3">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link to={`/services/${r.slug}`} className="flex items-center gap-3 text-sm text-[#4B5563] hover:text-[#0D72B8] transition-colors group">
                          <r.icon size={16} className="text-[#0D72B8] flex-shrink-0" />
                          <span className="group-hover:translate-x-0.5 transition-transform">{r.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 flex justify-between">
          {prev ? (
            <Link to={`/services/${prev.slug}`} className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#0D72B8] transition-colors">
              <ArrowLeft size={14} /> {prev.title}
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/services/${next.slug}`} className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#0D72B8] transition-colors">
              {next.title} <ArrowRight size={14} />
            </Link>
          ) : <div />}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D72B8] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-['Sora'] font-semibold text-white mb-4">{service.ctaText || "Ready to get started?"}</h2>
          <p className="text-blue-100 max-w-lg mx-auto mb-8">Let us help you implement {service.title.toLowerCase()} that drive real business results.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0D72B8] font-semibold text-sm rounded-full hover:bg-gray-50 transition-all shadow-lg">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
